import type { InstallationStatus, WarrantyStatus } from '@/types/asset'

// Color codes per spec: Expired = Red, <30 days = Orange, 30-90 days = Yellow,
// Active = Green, Not set = Neutral.
const WARRANTY_STATUS_META: Record<WarrantyStatus, { label: string; badgeClass: string; dotClass: string }> = {
  expired: {
    label: 'Expired',
    badgeClass: 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-400',
    dotClass: 'bg-red-500'
  },
  'expiring-30': {
    label: 'Expiring < 30 days',
    badgeClass: 'bg-orange-100 text-orange-800 dark:bg-orange-500/15 dark:text-orange-400',
    dotClass: 'bg-orange-500'
  },
  'expiring-90': {
    label: 'Expiring 30-90 days',
    badgeClass: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/15 dark:text-yellow-400',
    dotClass: 'bg-yellow-500'
  },
  active: {
    label: 'Active',
    badgeClass: 'bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-400',
    dotClass: 'bg-green-500'
  },
  'not-set': {
    label: 'No Warranty',
    badgeClass: 'bg-gray-100 text-gray-600 dark:bg-gray-500/15 dark:text-gray-400',
    dotClass: 'bg-gray-400'
  }
}

export const getWarrantyStatusLabel = (status: WarrantyStatus): string =>
  WARRANTY_STATUS_META[status]?.label || status

export const getWarrantyStatusBadgeClass = (status: WarrantyStatus): string =>
  WARRANTY_STATUS_META[status]?.badgeClass || WARRANTY_STATUS_META['not-set'].badgeClass

export const getWarrantyStatusDotClass = (status: WarrantyStatus): string =>
  WARRANTY_STATUS_META[status]?.dotClass || WARRANTY_STATUS_META['not-set'].dotClass

export const WARRANTY_STATUS_FILTER_OPTIONS: Array<{ label: string; value: WarrantyStatus }> = [
  { label: 'Expired', value: 'expired' },
  { label: 'Expiring in 30 days', value: 'expiring-30' },
  { label: 'Expiring in 90 days', value: 'expiring-90' },
  { label: 'Active Warranty', value: 'active' }
]

const INSTALLATION_STATUS_META: Record<InstallationStatus, { label: string; badgeClass: string }> = {
  installed: {
    label: 'Installed',
    badgeClass: 'bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-400'
  },
  'not-installed': {
    label: 'Not Installed',
    badgeClass: 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-400'
  },
  unknown: {
    label: 'Unknown',
    badgeClass: 'bg-gray-100 text-gray-600 dark:bg-gray-500/15 dark:text-gray-400'
  }
}

export const getInstallationStatusLabel = (status: InstallationStatus): string =>
  INSTALLATION_STATUS_META[status]?.label || status

export const getInstallationStatusBadgeClass = (status: InstallationStatus): string =>
  INSTALLATION_STATUS_META[status]?.badgeClass || INSTALLATION_STATUS_META.unknown.badgeClass

export const INSTALLATION_STATUS_FILTER_OPTIONS: Array<{ label: string; value: InstallationStatus }> = [
  { label: 'Installed', value: 'installed' },
  { label: 'Not Installed', value: 'not-installed' },
  { label: 'Unknown', value: 'unknown' }
]

export const formatWarrantyDuration = (years: number, months: number): string => {
  if (!years && !months) return 'Not set'
  const parts: string[] = []
  if (years) parts.push(`${years} Year${years > 1 ? 's' : ''}`)
  if (months) parts.push(`${months} Month${months > 1 ? 's' : ''}`)
  return parts.join(' ')
}

export const formatRemainingDays = (remainingDays: number | null): string => {
  if (remainingDays === null) return 'N/A'
  if (remainingDays < 0) return `Expired ${Math.abs(remainingDays)} day${Math.abs(remainingDays) !== 1 ? 's' : ''} ago`
  if (remainingDays === 0) return 'Expires today'
  return `${remainingDays} day${remainingDays !== 1 ? 's' : ''} left`
}

export const formatAssetDate = (value: string | null): string => {
  if (!value) return 'N/A'
  const date = new Date(value)
  if (isNaN(date.getTime())) return 'N/A'
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}
